import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { X } from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import SecureImage from './SecureImage';
import StatusIndicator from './StatusIndicator';
import { resolveCardStatus } from '../utils/pidStatus';
import {
  Overlay,
  PIDDisPanel,
  PIDDisHeader,
  PIDDisTitle,
  CloseButton,
  PIDDisBodyScroll,
  PIDDisLayout,
  PIDDisMediaColumn,
  PIDDisDivider,
  PIDDisDetailsColumn,
  HighlightedTitle,
  InfoRow,
  InfoLabel,
  InfoValue,
  CarouselNavButton,
  CarouselWrapper,
  PIDDisDetailGrid,
  PIDDisImageBox,
  LightboxBackdrop,
  LightboxCloseButton,
  LightboxImage,
} from '../styles/PackageIndexStyles';

const parseDims = (dim) => {
  if (!dim) return { L: '', W: '', H: '' };
  const [L = '', W = '', H = ''] = String(dim).split(',').map(s => s.trim());
  return { L, W, H };
};

const formatDisplay = ({ L, W, H }) => {
  if (!L && !W && !H) return '';
  return `${L || ''} x ${W || ''} x ${H || ''}`;
};

const PIDDISPLAYS_CDN_BASE = 'https://thevaultmedia.azureedge.net/vault-images/PIDDisplays/';
const toPidDisplaysCdnUrl = (src) => {
  if (!src) return '';
  const s = String(src);
  if (/^https?:\/\//i.test(s)) return s;
  const cleaned = s.split('?')[0].split('#')[0].trim();
  const filename = cleaned.split('/').filter(Boolean).pop() || cleaned;
  const encoded = encodeURIComponent(filename).replace(/%2F/g, '/');
  return `${PIDDISPLAYS_CDN_BASE}${encoded}`;
};

const DisplayViewerPanel = ({ group, onClose }) => {
  const panelRef = useRef();
  const [current, setCurrent] = useState(0);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const imagesToDisplay = useMemo(() => {
    const imgs = Array.isArray(group?.Images) ? group.Images : [];
    if (imgs.length > 0) return imgs;
    return ['https://thevaultmedia.blob.core.windows.net/vault-images/PIDDisplays/DisplayFallback.webp'];
  }, [group]);

  const max = imagesToDisplay.length;

  useEffect(() => {
    imagesToDisplay.forEach(src => {
      const img = new Image();
      img.src = toPidDisplaysCdnUrl(src);
    });
  }, [imagesToDisplay]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => setCurrent((c) => (c + 1) % max),
    onSwipedRight: () => setCurrent((c) => (c - 1 + max) % max),
    trackMouse: true,
  });

  const handleClickOutside = useCallback(
    (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    },
    [onClose]
  );

  const handleEscape = useCallback((e) => {
    if (e.key !== 'Escape') return;
    if (lightboxSrc) {
      setLightboxSrc(null);
      return;
    }
    onClose();
  }, [onClose, lightboxSrc]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [handleClickOutside, handleEscape]);

  const formedDims = parseDims(group?.DimensionsFormed);
  const flatDims = parseDims(group?.DimensionsFlat);

  return (
    <Overlay>
      <PIDDisPanel ref={panelRef}>
        <PIDDisHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0 }}>
            <PIDDisTitle>{group?.ProductID || 'Display'}</PIDDisTitle>
            <StatusIndicator status={resolveCardStatus(group)} />
          </div>
          <CloseButton className="PIDDis-CloseButton" onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </PIDDisHeader>

        <PIDDisBodyScroll>
          <PIDDisLayout>
            <PIDDisMediaColumn>
              <CarouselWrapper {...swipeHandlers}>
                <div style={{ position: 'relative', width: '100%' }}>
                  {max > 1 && current > 0 && (
                    <CarouselNavButton
                      left
                      onClick={() => setCurrent((current - 1 + max) % max)}
                    >
                      &#8249;
                    </CarouselNavButton>
                  )}
                  {max > 1 && current < max - 1 && (
                    <CarouselNavButton
                      right
                      onClick={() => setCurrent((current + 1) % max)}
                    >
                      &#8250;
                    </CarouselNavButton>
                  )}

                  <PIDDisImageBox className="PIDDis-ImageBox">
                    <SecureImage
                      src={imagesToDisplay[current]}
                      folder="PIDDisplays"
                      alt={group?.ProductID || group?.Description || 'Display image'}
                      onClick={() => setLightboxSrc(toPidDisplaysCdnUrl(imagesToDisplay[current]))}
                    />
                  </PIDDisImageBox>

                  {max > 1 && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '12px',
                        color: 'var(--white)',
                        fontSize: '0.85rem',
                        background: 'var(--shadow-a35)',
                        padding: '2px 6px',
                        borderRadius: '6px',
                      }}
                    >
                      {current + 1} / {max}
                    </div>
                  )}
                </div>
              </CarouselWrapper>
            </PIDDisMediaColumn>

            <PIDDisDivider />

            <PIDDisDetailsColumn>
              <HighlightedTitle>Display Details</HighlightedTitle>
              <PIDDisDetailGrid className="PIDDis-DetailGrid">
                <InfoRow style={{ gridColumn: '1 / -1' }}>
                  <InfoLabel>Description</InfoLabel>
                  <InfoValue>{group?.Description || '-'}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>Item Number</InfoLabel>
                  <InfoValue>{group?.ItemNumber || '-'}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>Count (Per Pallet)</InfoLabel>
                  <InfoValue>{group?.CountPerPallet ?? '-'}</InfoValue>
                </InfoRow>

                <InfoRow style={{ gridColumn: '1 / -1' }}>
                  <InfoLabel>Dimensions (Formed)</InfoLabel>
                  <InfoValue>{formatDisplay(formedDims) || '-'}</InfoValue>
                </InfoRow>

                <InfoRow style={{ gridColumn: '1 / -1' }}>
                  <InfoLabel>Dimensions (Flat)</InfoLabel>
                  <InfoValue>{formatDisplay(flatDims) || '-'}</InfoValue>
                </InfoRow>
              </PIDDisDetailGrid>
            </PIDDisDetailsColumn>
          </PIDDisLayout>
        </PIDDisBodyScroll>
      </PIDDisPanel>

      {lightboxSrc && (
        <LightboxBackdrop
          className="PIDDis-LightboxBackdrop"
          onClick={() => setLightboxSrc(null)}
          role="dialog"
          aria-modal="true"
        >
          <LightboxCloseButton
            type="button"
            className="PIDDis-LightboxCloseButton"
            aria-label="Close preview"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxSrc(null);
            }}
          >
            ×
          </LightboxCloseButton>
          <div onClick={(e) => e.stopPropagation()}>
            <LightboxImage src={lightboxSrc} alt="Preview" />
          </div>
        </LightboxBackdrop>
      )}
    </Overlay>
  );
};

export default React.memo(DisplayViewerPanel);

